import Drawflow from 'drawflow';

class FlowBuilderManager {
    constructor() {
        this.editor = null;
        this.container = null;
        this.templateId = null;
        this.nodeIdCounter = 1;
        this.zoomLevel = 1;
        this.initialized = false;
        this.isLoadingExistingData = false; // Flag to prevent creating DB records when loading
        this._reflowTimeout = null;
        this._updateConnectionsTimeout = null;
    }

    init(containerId, templateId, wire = null) {
        if (this.initialized) {
            return;
        }

        this.container = document.getElementById(containerId);
        this.templateId = templateId;
        this.$wire = wire;

        if (!this.container) {
            return;
        }

        this.initialized = true;

        this.editor = new Drawflow(this.container);
        this.editor.reroute = false;
        this.editor.force_first_input = false;
        this.editor.line_path = 5;
        this.editor.editor_mode = 'edit';

        const editor = this.editor;

        this.editor.createCurvature = function(start_pos_x, start_pos_y, end_pos_x, end_pos_y, curvature_value, type) {
            const center_y = ((end_pos_y - start_pos_y) / 2) + start_pos_y;

            return ' M ' + start_pos_x + ' ' + start_pos_y +
                   ' L ' + start_pos_x + ' ' + center_y +
                   ' L ' + end_pos_x + ' ' + center_y +
                   ' L ' + end_pos_x + ' ' + end_pos_y;
        };

        this.editor.start();

        this.setupEventListeners();
        this.setupZoomControls();
        this.setupDragMonitoring();
        this.addDottedBackground();
    }

    setupEventListeners() {
        if (this.$wire) {
            this.$wire.on('flowBuilder:nodeCreatedSuccess', (eventData) => {
                const data = Array.isArray(eventData) ? eventData[0] : eventData;
                const node = this.editor.getNodeFromId(data.nodeId);
                if (node) {
                    node.data.stepId = data.stepId;

                    try {
                        // Remove loading state from the node
                        this.hideNodeLoading(data.nodeId);

                        // Update the node content to enable the edit button
                        this.updateNodeContent(data.nodeId, node.data);
                    } catch (error) {
                        console.error('Failed to update node after creation:', error);

                        // Ensure loading state is cleared even if update fails
                        try {
                            this.hideNodeLoading(data.nodeId);
                        } catch (cleanupError) {
                            console.error('Failed to clear loading state:', cleanupError);
                        }

                        // Show error state on the node
                        const nodeContentElement = document.querySelector(`#node-${data.nodeId} .drawflow_content_node`);
                        if (nodeContentElement) {
                            const errorHtml = `
                                <div class="p-4 bg-red-50 border border-red-200 rounded">
                                    <p class="text-red-600 text-sm">Failed to load node content</p>
                                    <button class="mt-2 text-xs text-red-700 underline" onclick="location.reload()">
                                        Reload page
                                    </button>
                                </div>
                            `;
                            nodeContentElement.innerHTML = errorHtml;
                        }
                    }

                    // If this node should auto-open the approvers modal
                    if (node.data.autoOpenModal) {
                        delete node.data.autoOpenModal;
                        this.openApproversModal(data.stepId);
                    }

                    // A new child changes its parent's trailing-chip state ("Runs
                    // before final approval") — regenerate the parent card, since
                    // adding a child only reflows positions, not node HTML.
                    if (node.data.parentStepId) {
                        this.updateNodeByStepId(node.data.parentStepId);
                    }
                }
            });

            // Re-parent accepted: refresh BOTH parents' cards (their trailing
            // chips may have flipped) and reflow the tree around the new shape.
            this.$wire.on('flowBuilder:connectionApplied', (eventData) => {
                const data = Array.isArray(eventData) ? eventData[0] : eventData;
                if (data.oldParentStepId) {
                    this.updateNodeByStepId(data.oldParentStepId);
                }
                if (data.newParentStepId) {
                    this.updateNodeByStepId(data.newParentStepId);
                }
                this.debouncedReflow(100);
            });

            // Re-parent rejected (would create a cycle): undo the drawn line.
            this.$wire.on('flowBuilder:connectionRejected', () => {
                const c = this.lastDrawnConnection;
                if (c) {
                    this.editor.removeSingleConnection(c.output_id, c.input_id, c.output_class, c.input_class);
                    this.lastDrawnConnection = null;
                }
                this.debouncedReflow(100);
            });
        }

        this.editor.on('nodeCreated', (id) => {
            if (!this.isLoadingExistingData) {
                const node = this.editor.getNodeFromId(id);
                const parentStepId = node?.data?.parentStepId || null;

                this.syncWithLivewire('nodeCreated', {
                    nodeId: id,
                    parentStepId: parentStepId
                });
            }
        });

        this.editor.on('nodeRemoved', (id) => {
            // Reflow the tree after any node removal to maintain proper spacing
            this.debouncedReflow(100);
        });

        this.editor.on('nodeSelected', (id) => {
            this.syncWithLivewire('nodeSelected', { nodeId: id });
        });

        this.editor.on('connectionCreated', (connection) => {
            // Programmatic connections during canvas load already mirror the
            // stored tree — only user-drawn / splice connections need syncing.
            if (this.isLoadingExistingData) {
                return;
            }

            const sourceNode = this.editor.getNodeFromId(connection.output_id);
            const targetNode = this.editor.getNodeFromId(connection.input_id);

            // Single parent: drawing a new line into a node replaces its old
            // one — remove every OTHER connection still attached to this input
            // so the canvas can't show two parents while the data has one.
            const otherInputs = (targetNode?.inputs?.[connection.input_class]?.connections || [])
                .filter((c) => String(c.node) !== String(connection.output_id));
            otherInputs.forEach((c) => {
                this.editor.removeSingleConnection(c.node, connection.input_id, c.input, connection.input_class);
            });

            // Remember the drawn line so a server rejection (cycle) can undo it.
            this.lastDrawnConnection = {
                output_id: connection.output_id,
                input_id: connection.input_id,
                output_class: connection.output_class,
                input_class: connection.input_class,
            };

            const connectionData = {
                sourceStepId: sourceNode?.data?.stepId || null,
                targetStepId: targetNode?.data?.stepId || null,
                sourceIsStart: sourceNode?.data?.type === 'start' || sourceNode?.name === 'start',
                output_id: connection.output_id,
                input_id: connection.input_id
            };

            this.syncWithLivewire('connectionCreated', connectionData);
        });

        this.editor.on('connectionRemoved', (connection) => {
            const targetNode = this.editor.getNodeFromId(connection.input_id);

            const connectionData = {
                targetStepId: targetNode?.data?.stepId || null,
                input_id: connection.input_id,
                output_id: connection.output_id
            };

            this.syncWithLivewire('connectionRemoved', connectionData);
        });

        this.editor.on('nodeMoved', (id) => {
            const node = this.editor.getNodeFromId(id);
            if (node) {
                requestAnimationFrame(() => {
                    this.editor.updateConnectionNodes(`node-${id}`);
                });

                this.syncWithLivewire('nodeMoved', {
                    nodeId: id,
                    position: {
                        x: node.pos_x,
                        y: node.pos_y
                    }
                });
            }
        });
    }

    setupDragMonitoring() {
        // Monitor mouse movements during drag to update connections in real-time
        let isDragging = false;
        let draggedNodeId = null;

        this.container.addEventListener('mousedown', (e) => {
            // Check if we're clicking on a node
            const nodeElement = e.target.closest('.drawflow-node');
            if (nodeElement) {
                isDragging = true;
                draggedNodeId = nodeElement.id.replace('node-', '');
            }
        });

        this.container.addEventListener('mousemove', (e) => {
            if (isDragging && draggedNodeId) {
                // Update connections during drag for smooth movement
                requestAnimationFrame(() => {
                    this.editor.updateConnectionNodes(`node-${draggedNodeId}`);
                });
            }
        });

        this.container.addEventListener('mouseup', (e) => {
            if (isDragging && draggedNodeId) {
                // Final update when drag ends
                requestAnimationFrame(() => {
                    this.editor.updateConnectionNodes(`node-${draggedNodeId}`);
                });
            }
            isDragging = false;
            draggedNodeId = null;
        });
    }

    setupZoomControls() {
        // Mouse wheel zoom
        this.container.addEventListener('wheel', (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? 0.9 : 1.1;
                this.zoom(delta);
            }
        }, { passive: false });

        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
                e.preventDefault();
                this.zoomIn();
            } else if ((e.ctrlKey || e.metaKey) && e.key === '-') {
                e.preventDefault();
                this.zoomOut();
            } else if ((e.ctrlKey || e.metaKey) && e.key === '0') {
                e.preventDefault();
                this.zoomReset();
            }
        });
    }

    zoom(factor) {
        this.zoomLevel *= factor;
        this.zoomLevel = Math.max(0.3, Math.min(2, this.zoomLevel));
        this.editor.zoom = this.zoomLevel;
        this.editor.zoom_refresh();
    }

    zoomIn() {
        this.zoom(1.1);
    }

    zoomOut() {
        this.zoom(0.9);
    }

    zoomReset() {
        this.zoomLevel = 1;
        this.editor.zoom = 1;
        this.editor.zoom_reset();
    }

    addDottedBackground() {
        // Add CSS class for dotted background
        this.container.classList.add('flow-builder-canvas');
    }

    addNode(type, posX = null, posY = null, data = {}) {
        // Flag to track if position was auto-generated (for centering)
        let shouldCenter = false;

        // Use a temporary counter for positioning
        const tempId = this.nodeIdCounter++;

        if (posX === null) {
            posX = 100 + (tempId * 50);
            shouldCenter = true; // Auto-positioned nodes should be centered
        }
        if (posY === null) posY = 100 + (tempId * 50);

        let html = '';
        let inputs = 1;
        let outputs = 1;

        // Generate HTML with a placeholder ID first
        switch(type) {
            case 'start':
                inputs = 0;
                outputs = 1;
                html = this.getStartNodeHtml('PLACEHOLDER', data);
                break;
            case 'approval':
                html = this.getApprovalNodeHtml('PLACEHOLDER', data);
                break;
            case 'job':
                html = this.getJobNodeHtml('PLACEHOLDER', data);
                break;
            case 'lookup':
                // Lookup ("approval table") steps render as an approval node with a
                // "Routed by table" badge - they are still approval steps to the user.
                data.routedByTable = true;
                html = this.getApprovalNodeHtml('PLACEHOLDER', data);
                break;
            case 'unconfigured':
                html = this.getUnconfiguredNodeHtml('PLACEHOLDER', data);
                break;
            case 'conditional':
                outputs = 2; // Two outputs for true/false branches
                html = this.getConditionalNodeHtml('PLACEHOLDER', data);
                break;
            default:
                html = this.getDefaultNodeHtml('PLACEHOLDER', data);
        }

        // editor.addNode returns the actual Drawflow node ID (integer)
        const nodeId = this.editor.addNode(
            type,
            inputs,
            outputs,
            posX,
            posY,
            `node-${type}`,
            data,
            html
        );

        // Update the HTML to use the real node ID for button handlers
        const nodeElement = this.editor.container.querySelector(`#node-${nodeId}`);
        if (nodeElement) {
            // Replace placeholder IDs in onclick handlers
            const htmlContent = nodeElement.innerHTML;
            nodeElement.innerHTML = htmlContent.replace(/PLACEHOLDER/g, nodeId);
        }

        // If this is a newly created node without a stepId, show loading state
        if (!data.stepId && !this.isLoadingExistingData) {
            this.showNodeLoading(nodeId);
        }

        // If node was auto-positioned (from toolbar), center and select it
        if (shouldCenter) {
            setTimeout(() => {
                this.centerNodeInView(nodeId);
                this.selectNode(nodeId);
            }, 100);
        }

        return nodeId;
    }

    /**
     * Escape a value for safe interpolation into node markup that is later assigned
     * via innerHTML. Node data (step names, field labels, approver/user/group names,
     * condition text) is user-controlled and would otherwise allow stored XSS. Covers
     * both element-text and double-quoted-attribute (title="...") contexts.
     */
    escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    getStartNodeHtml(nodeId, data) {
        return `
            <div class="flow-node start-node">
                <div class="node-header">
                    <i class="fa fa-play-circle"></i>
                    <span>Start: ${this.escapeHtml(data.templateType || 'Form')}</span>
                </div>
                <div class="node-body">
                    <div class="text-sm text-gray-600">
                        ${this.escapeHtml(data.templateName || 'Workflow')}
                    </div>
                </div>
                <!-- Add child button positioned at bottom edge -->
                <button
                    onclick="window.flowBuilder.addChildNode(${nodeId})"
                    class="add-child-btn"
                    title="Add the first step">
                    <i class="fa fa-plus"></i>
                </button>
            </div>
        `;
    }

    getApprovalNodeHtml(nodeId, data) {
        const displayName = this.escapeHtml(data.name || 'Approval Step');
        const stepId = data.stepId || null;

        // Build the node body. When the step routes via an approval table, show a
        // "Routed by table" summary instead of a static approver list.
        let approversHtml = '';
        if (data.routedByTable) {
            const lookupField = this.escapeHtml(data.lookupFieldName || 'No field selected');
            const entryCount = data.lookupEntryCount ?? 0;
            const fallback = (data.fallbackBehavior || 'skip') === 'use_fallback' ? 'Fallback approver' : 'Skip if no match';
            approversHtml = `
                <div class="node-routed-badge"><i class="fa fa-table"></i> Routed by table</div>
                <div class="approver-item"><i class="fa fa-search"></i><span>${lookupField}</span></div>
                <div class="approver-item"><i class="fa fa-list"></i><span>${this.escapeHtml(entryCount)} ${entryCount === 1 ? 'mapping' : 'mappings'}</span></div>
                <div class="approver-item"><i class="fa fa-shield"></i><span>${fallback}</span></div>
            `;
        } else if (data.approversList && Array.isArray(data.approversList) && data.approversList.length > 0) {
            const iconMap = {
                group: 'fa-users',
                manager: 'fa-sitemap',
                field: 'fa-file-alt',
                user: 'fa-user',
            };
            const approverItems = data.approversList.map(approver => {
                const icon = iconMap[approver.type] || 'fa-user';
                return `<div class="approver-item"><i class="fa ${icon}"></i><span>${this.escapeHtml(approver.name)}</span></div>`;
            }).join('');
            approversHtml = approverItems;
        } else {
            approversHtml = `<div class="approver-empty">No approvers configured</div>`;
        }

        // Build compact status indicators for the header bar
        const indicators = [];
        if (data.requiresEsignature) {
            indicators.push(`<span class="node-indicator node-indicator-esign" title="E-Signature required"><i class="fa fa-pen-nib"></i> E-Sign</span>`);
        }
        const quorumType = data.quorumType || 'all';
        if (quorumType !== 'all') {
            const quorumLabels = { majority: 'Majority', fraction: 'Fraction', count: 'Count' };
            const label = this.escapeHtml(quorumLabels[quorumType] || quorumType);
            indicators.push(`<span class="node-indicator node-indicator-quorum" title="${label} approval required">${label}</span>`);
        }
        const indicatorsHtml = indicators.length > 0 ? `<div class="node-indicators">${indicators.join('')}</div>` : '';

        return `
            <div class="flow-node approval-node">
                <div class="node-header">
                    <i class="fa fa-user-check"></i>
                    <span title="${displayName}">${displayName}</span>
                    <button
                        onclick="window.flowBuilder.openApproversModal(${stepId || 'null'})"
                        class="node-edit-btn"
                        ${!stepId ? 'disabled' : ''}
                        title="${stepId ? 'Configure step' : 'Loading...'}">
                        <i class="fa fa-pencil"></i>
                    </button>
                    <button
                        onclick="window.flowBuilder.removeNode(${nodeId})"
                        class="node-delete-btn"
                        title="Delete this step">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                ${indicatorsHtml}
                <div class="node-body">
                    <div class="approvers-list">
                        ${approversHtml}
                    </div>
                </div>
                <!-- Add child button positioned at bottom edge -->
                <button
                    onclick="window.flowBuilder.addChildNode(${nodeId})"
                    class="add-child-btn"
                    title="Add a new step below this one">
                    <i class="fa fa-plus"></i>
                </button>
            </div>
        `;
    }

    getJobNodeHtml(nodeId, data) {
        const displayName = this.escapeHtml(data.name || 'Automated action');
        const actionLabel = this.escapeHtml(data.actionLabel || 'Not configured');
        const summary = data.summary ? this.escapeHtml(data.summary) : null;
        const stepId = data.stepId || null;

        // Compact status indicators for the header bar (approval-node pattern)
        const indicators = [];
        if (data.tested) {
            indicators.push(`<span class="node-indicator node-indicator-tested" title="A test delivery succeeded"><i class="fa fa-check"></i> Tested</span>`);
        }
        if (data.hasChildren === false) {
            indicators.push(`<span class="node-indicator node-indicator-final" title="Runs once the request is approved — a failure here never blocks the approval."><i class="fa fa-flag-checkered"></i> Runs after final approval</span>`);
        }
        const indicatorsHtml = indicators.length > 0 ? `<div class="node-indicators">${indicators.join('')}</div>` : '';

        const bodyRows = [
            `<div class="approver-item"><i class="fa fa-bolt"></i><span>${actionLabel}</span></div>`,
        ];
        if (summary) {
            const summaryIcon = data.actionSlug === 'send_email' ? 'fa-envelope' : 'fa-link';
            bodyRows.push(`<div class="approver-item"><i class="fa ${summaryIcon}"></i><span>${summary}</span></div>`);
        } else {
            const emptyText = data.actionSlug === 'send_email' ? 'Not set up — add recipients' : 'Not set up — add a destination URL';
            bodyRows.push(`<div class="approver-empty">${emptyText}</div>`);
        }

        return `
            <div class="flow-node job-node">
                <div class="node-header">
                    <i class="fa fa-bolt"></i>
                    <span title="${displayName}">${displayName}</span>
                    <button
                        onclick="window.flowBuilder.openApproversModal(${stepId || 'null'})"
                        class="node-edit-btn"
                        ${!stepId ? 'disabled' : ''}
                        title="${stepId ? 'Configure step' : 'Loading...'}">
                        <i class="fa fa-pencil"></i>
                    </button>
                    <button
                        onclick="window.flowBuilder.removeNode(${nodeId})"
                        class="node-delete-btn"
                        title="Delete this step">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                ${indicatorsHtml}
                <div class="node-body">
                    <div class="approvers-list">
                        ${bodyRows.join('')}
                    </div>
                </div>
                <!-- Add child button positioned at bottom edge -->
                <button
                    onclick="window.flowBuilder.addChildNode(${nodeId})"
                    class="add-child-btn"
                    title="Add a new step below this one">
                    <i class="fa fa-plus"></i>
                </button>
            </div>
        `;
    }

    getUnconfiguredNodeHtml(nodeId, data) {
        const displayName = this.escapeHtml(data.name || 'New Step');
        const stepId = data.stepId || null;

        return `
            <div class="flow-node approval-node unconfigured-node">
                <div class="node-header">
                    <i class="fa fa-circle-question"></i>
                    <span title="${displayName}">${displayName}</span>
                    <button
                        onclick="window.flowBuilder.openApproversModal(${stepId || 'null'})"
                        class="node-edit-btn"
                        ${!stepId ? 'disabled' : ''}
                        title="${stepId ? 'Set up this step' : 'Loading...'}">
                        <i class="fa fa-pencil"></i>
                    </button>
                    <button
                        onclick="window.flowBuilder.removeNode(${nodeId})"
                        class="node-delete-btn"
                        title="Delete this step">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="node-body">
                    <div class="approver-empty">Unconfigured — choose approvers, an approval table, or an automated action</div>
                </div>
                <!-- Add child button positioned at bottom edge -->
                <button
                    onclick="window.flowBuilder.addChildNode(${nodeId})"
                    class="add-child-btn"
                    title="Add a new step below this one">
                    <i class="fa fa-plus"></i>
                </button>
            </div>
        `;
    }

    getConditionalNodeHtml(nodeId, data) {
        const displayName = this.escapeHtml(data.name || 'Conditional');
        const stepId = data.stepId || null;

        return `
            <div class="flow-node conditional-node">
                <div class="node-header">
                    <i class="fa fa-code-branch"></i>
                    <span title="${displayName}">${displayName}</span>
                    <button
                        onclick="window.flowBuilder.removeNode(${nodeId})"
                        class="node-delete-btn"
                        title="Delete this step">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="node-body">
                    <div class="condition-section">
                        <div class="condition-header">
                            <span class="font-semibold text-gray-700 text-sm">Condition:</span>
                            ${stepId ? `<button
                                onclick="window.flowBuilder.openConditionConfigModal(${stepId})"
                                class="condition-edit-btn"
                                title="Configure condition">
                                <i class="fa fa-pencil"></i>
                            </button>` : ''}
                        </div>
                        <div class="condition-details">
                            <div class="text-gray-600 text-xs">${this.escapeHtml(data.condition || 'Not configured')}</div>
                        </div>
                    </div>
                </div>
                <!-- Two add child buttons for true/false branches -->
                <button
                    onclick="window.flowBuilder.addChildNode(${nodeId}, 'true')"
                    class="add-child-btn add-true-btn"
                    title="Add step for TRUE condition">
                    <i class="fa fa-check"></i>
                </button>
                <button
                    onclick="window.flowBuilder.addChildNode(${nodeId}, 'false')"
                    class="add-child-btn add-false-btn"
                    title="Add step for FALSE condition">
                    <i class="fa fa-times"></i>
                </button>
            </div>
        `;
    }

    getDefaultNodeHtml(nodeId, data) {
        return `
            <div class="flow-node default-node">
                <div class="node-header">
                    <i class="fa fa-cube"></i>
                    <span>${this.escapeHtml(data.title || 'Node')}</span>
                    <button onclick="window.flowBuilder.removeNode(${nodeId})" class="btn-remove">
                        <i class="fa fa-minus"></i>
                    </button>
                </div>
                <div class="node-body">
                    <div class="text-sm text-gray-600">
                        ${this.escapeHtml(data.description || 'Configure this node')}
                    </div>
                </div>
                <div class="node-actions">
                    <button onclick="window.flowBuilder.addChildNode(${nodeId})" class="btn-add">
                        <i class="fa fa-plus"></i>
                    </button>
                </div>
            </div>
        `;
    }

    async removeNode(nodeId) {
        if (confirm('Remove this step? Steps beneath it will reconnect to the step above.')) {
            const node = this.editor.getNodeFromId(nodeId);
            const stepId = node?.data?.stepId || null;

            // Capture the parent AND children BEFORE removal: the server splices
            // (children reattach to the deleted step's parent), so the canvas
            // redraws those connections after the delete instead of dropping the
            // subtree; the parent card also needs a refresh (trailing chip).
            const parentConnection = node?.inputs?.input_1?.connections?.[0];
            const parentNode = parentConnection ? this.editor.getNodeFromId(parentConnection.node) : null;
            const parentNodeId = parentConnection ? parentConnection.node : null;
            const parentStepId = parentNode?.data?.stepId || null;
            const childNodeIds = (node?.outputs?.output_1?.connections || []).map((c) => c.node);

            // Add fade-out animation to the node
            const nodeElement = document.getElementById(`node-${nodeId}`);
            if (nodeElement) {
                nodeElement.style.transition = 'opacity 0.3s ease-out';
                nodeElement.style.opacity = '0.3';
            }

            // Optimistically remove the node after a brief fade
            setTimeout(() => {
                this.editor.removeNodeId(`node-${nodeId}`);

                // Reflow the tree after removing the node (debounced to coalesce with nodeRemoved event)
                this.debouncedReflow(50);
            }, 150);

            // Then handle the backend deletion asynchronously
            if (stepId && this.$wire) {
                try {
                    await this.$wire.handleNodeRemoved({
                        nodeId: nodeId,
                        stepId: stepId
                    });

                    // Redraw the spliced connections: the deleted step's children
                    // now hang from its parent (a step, or the Start node for a
                    // root step). The server already spliced, so SUPPRESS the
                    // per-connection sync — those redundant serial roundtrips
                    // froze the canvas for seconds on larger deletes.
                    if (parentNodeId) {
                        this.isLoadingExistingData = true;
                        try {
                            childNodeIds.forEach((childNodeId) => {
                                this.editor.addConnection(parentNodeId, childNodeId, 'output_1', 'input_1');
                            });
                        } finally {
                            this.isLoadingExistingData = false;
                        }
                    }

                    // Refresh the parent card AFTER the backend delete so its
                    // hasChildren state is computed against the updated tree.
                    if (parentStepId) {
                        this.updateNodeByStepId(parentStepId);
                    }
                    this.debouncedReflow(100);
                } catch (error) {
                    console.error('Failed to delete node from backend:', error);

                    // On error, we could restore the node, but that's complex
                    // For now, just alert the user
                    alert('Failed to delete the node from the server. Please refresh the page.');
                }
            }
        }
    }

    removeChildNodesByStepIds(stepIds) {
        const allNodes = this.editor.drawflow.drawflow.Home.data;

        Object.keys(allNodes).forEach(nodeId => {
            const node = allNodes[nodeId];
            if (node.data && stepIds.includes(node.data.stepId)) {
                this.editor.removeNodeId(`node-${nodeId}`);
            }
        });

        // Reflow the tree after removing nodes to maintain proper spacing (debounced to coalesce with per-node nodeRemoved events)
        this.debouncedReflow(100);
    }

    showNodeLoading(nodeId) {
        const nodeElement = document.querySelector(`#node-${nodeId} .drawflow_content_node`);
        if (nodeElement) {
            // Store current content
            const currentContent = nodeElement.innerHTML;
            nodeElement.dataset.originalContent = currentContent;

            // Add loading overlay
            const loadingHtml = `
                <div class="flow-node-loading">
                    <div class="absolute inset-0 bg-white/75 rounded flex items-center justify-center z-20">
                        <svg class="animate-spin h-6 w-6 text-emerald-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    ${currentContent}
                </div>
            `;
            nodeElement.innerHTML = loadingHtml;
        }
    }

    hideNodeLoading(nodeId) {
        const nodeElement = document.querySelector(`#node-${nodeId} .drawflow_content_node`);
        if (nodeElement) {
            // Remove loading overlay if it exists
            const loadingOverlay = nodeElement.querySelector('.flow-node-loading');
            if (loadingOverlay) {
                // Get the original content without the loading overlay
                const originalContent = nodeElement.dataset.originalContent;
                if (originalContent) {
                    // Don't restore original content here - let updateNodeContent handle it
                    delete nodeElement.dataset.originalContent;
                }
            }
        }
    }

    async updateNodeByStepId(stepId) {
        // Find the node with this stepId
        const allNodes = this.editor.drawflow.drawflow.Home.data;
        let targetNodeId = null;

        Object.keys(allNodes).forEach(nodeId => {
            const node = allNodes[nodeId];
            if (node.data && node.data.stepId === stepId) {
                targetNodeId = nodeId;
            }
        });

        if (!targetNodeId) {
            console.warn('Node not found for stepId:', stepId);
            return;
        }

        // Show loading state on the node
        this.showNodeLoading(targetNodeId);

        // Fetch fresh step data from server
        if (!this.$wire) {
            console.error('$wire not available');
            this.hideNodeLoading(targetNodeId);
            return;
        }

        try {
            const stepData = await this.$wire.call('getStepData', stepId);
            if (stepData) {
                // Update the node's HTML with fresh data
                this.updateNodeContent(targetNodeId, stepData);

                // Reflow the tree after updating approvers to maintain proper spacing
                this.debouncedReflow(100);
            }
        } catch (error) {
            console.error('Failed to update node:', error);
        } finally {
            // Hide loading state
            this.hideNodeLoading(targetNodeId);
        }
    }

    updateNodeContent(nodeId, stepData) {
        const node = this.editor.getNodeFromId(nodeId);
        if (!node) return;

        // Generate new HTML based on step type
        let newHtml = '';
        if (stepData.type === 'approval') {
            newHtml = this.getApprovalNodeHtml(nodeId, stepData);
        } else if (stepData.type === 'lookup') {
            // Render as an approval node with the "Routed by table" badge.
            stepData.routedByTable = true;
            stepData.lookupFieldName = stepData.lookupFieldName || stepData.lookup_field_name;
            stepData.lookupEntryCount = stepData.lookupEntryCount ?? stepData.lookup_entry_count;
            stepData.fallbackBehavior = stepData.fallbackBehavior || stepData.fallback_behavior;
            newHtml = this.getApprovalNodeHtml(nodeId, stepData);
        } else if (stepData.type === 'unconfigured') {
            newHtml = this.getUnconfiguredNodeHtml(nodeId, stepData);
        } else if (stepData.type === 'job') {
            newHtml = this.getJobNodeHtml(nodeId, stepData);
        } else if (stepData.type === 'conditional') {
            newHtml = this.getConditionalNodeHtml(nodeId, stepData);
        }

        // Update the node's data
        this.editor.updateNodeDataFromId(nodeId, stepData);

        // Update the HTML in the Drawflow data structure
        this.editor.drawflow.drawflow.Home.data[nodeId].html = newHtml;

        // Update the DOM element directly - target the content node inside the drawflow node
        const nodeContentElement = document.querySelector(`#node-${nodeId} .drawflow_content_node`);
        if (nodeContentElement) {
            nodeContentElement.innerHTML = newHtml;
        }

        // Update connections after DOM update
        this.editor.updateConnectionNodes(`node-${nodeId}`);
    }

    addChildNode(parentNodeId, branch = null) {
        const parentNode = this.editor.getNodeFromId(parentNodeId);
        if (!parentNode) return;

        // New steps start unconfigured; the content type (approvers vs approval
        // table) is chosen in the modal that auto-opens once the step is created.
        const nodeType = 'unconfigured';

        const parentStepId = parentNode.data?.stepId || null;

        // Temporarily place the new node at parent position
        const newNodeId = this.addNode(nodeType, parentNode.pos_x, parentNode.pos_y + 200, {
            parentStepId: parentStepId,
            autoOpenModal: true // Flag to auto-open approvers modal once stepId is received
        });

        const outputIndex = branch === 'false' ? 2 : 1;
        this.editor.addConnection(parentNodeId, newNodeId, `output_${outputIndex}`, 'input_1');

        // After adding the node and connection, reflow the entire tree
        this.reflowEntireTree();

        // Smooth scroll to center and select the new node
        setTimeout(() => {
            this.centerNodeInView(newNodeId);
            this.selectNode(newNodeId);
        }, 150);
    }

    centerNodeInView(nodeId) {
        const node = this.editor.getNodeFromId(nodeId);
        if (!node) return;

        const container = this.editor.container;
        const canvasRect = container.getBoundingClientRect();

        // Get current zoom/scale
        const zoom = this.editor.zoom || 1;

        // Calculate the center position of the node
        const nodeCenterX = node.pos_x * zoom;
        const nodeCenterY = node.pos_y * zoom;

        // Calculate the scroll position to center the node
        const targetScrollLeft = nodeCenterX - (canvasRect.width / 2) + 120; // 120 is half node width
        const targetScrollTop = nodeCenterY - (canvasRect.height / 2) + 50; // 50 is approx half node height

        // Smooth scroll to the target position
        container.scrollTo({
            left: targetScrollLeft,
            top: targetScrollTop,
            behavior: 'smooth'
        });
    }

    selectNode(nodeId) {
        // First, remove any existing selections
        const selectedNodes = this.editor.container.querySelectorAll('.selected');
        selectedNodes.forEach(node => {
            node.classList.remove('selected');
        });

        // Select the new node by adding the 'selected' class
        const nodeElement = document.getElementById(`node-${nodeId}`);
        if (nodeElement) {
            nodeElement.classList.add('selected');

            // Also update Drawflow's internal selection state
            this.editor.node_selected = nodeElement;
            this.editor.dispatch('nodeSelected', nodeId);
        }
    }

    centerViewAtTop() {
        // Find the actual bounds of all nodes to center properly
        const allNodes = this.editor.drawflow.drawflow.Home.data;
        if (!allNodes || Object.keys(allNodes).length === 0) {
            return;
        }

        // Find the topmost node and calculate average X position
        let minY = Infinity;
        let totalX = 0;
        let nodeCount = 0;
        let topNodes = [];
        let startNode = null;

        // First pass: find the minimum Y position and identify start node
        Object.entries(allNodes).forEach(([nodeId, node]) => {
            if (node.pos_y < minY) {
                minY = node.pos_y;
            }
            // Check if this is the start node (no inputs)
            if (node.inputs && Object.keys(node.inputs).length === 0) {
                startNode = { id: nodeId, ...node };
            }
        });

        // Second pass: find all nodes at or near the top
        Object.values(allNodes).forEach(node => {
            if (node.pos_y <= minY + 50) { // Within 50px of the top
                topNodes.push(node);
                totalX += node.pos_x;
                nodeCount++;
            }
        });

        if (nodeCount === 0) return;

        // Calculate the average X position of top nodes
        const avgX = totalX / nodeCount;

        // The actual scrollable element is the Drawflow container itself
        const scrollableContainer = this.container;
        const canvasRect = scrollableContainer.getBoundingClientRect();

        // Get current zoom
        const zoom = this.editor.zoom || 1;

        // Get the actual Drawflow precanvas element which has the transform
        const precanvas = scrollableContainer.querySelector('.drawflow');
        const transform = precanvas ? window.getComputedStyle(precanvas).transform : 'none';

        // Parse the transform matrix to get the actual translate values
        let translateX = 0;
        let translateY = 0;
        if (transform && transform !== 'none') {
            const matrix = transform.match(/matrix\((.+)\)/);
            if (matrix) {
                const values = matrix[1].split(', ');
                translateX = parseFloat(values[4]) || 0;
                translateY = parseFloat(values[5]) || 0;
            }
        }

        // Drawflow uses transforms to position its canvas, not just scrolling
        // We need to adjust the transform to center the node in the viewport

        // Calculate desired center position in viewport
        const viewportCenterX = canvasRect.width / 2;
        const viewportTopWithMargin = 50;

        // Calculate what the new transform should be to position the node at viewport center/top
        // We want: (avgX * zoom) + newTranslateX = viewportCenterX
        // So: newTranslateX = viewportCenterX - (avgX * zoom)
        const newTranslateX = viewportCenterX - (avgX * zoom) - 120; // 120 is half node width
        const newTranslateY = viewportTopWithMargin - (minY * zoom);

        console.log('Centering view:', {
            startNode: startNode ? {
                id: startNode.id,
                position: { x: startNode.pos_x, y: startNode.pos_y },
                name: startNode.name,
                class: startNode.class
            } : 'No start node found',
            avgX,
            minY,
            zoom,
            canvasWidth: canvasRect.width,
            currentTransform: transform,
            currentTranslateX: translateX,
            currentTranslateY: translateY,
            newTranslateX,
            newTranslateY,
            nodeCount,
            topNodes: topNodes.map(n => ({ x: n.pos_x, y: n.pos_y }))
        });

        // Update Drawflow's canvas transform directly with animation
        if (precanvas) {
            // Add transition for smooth animation
            precanvas.style.transition = 'transform 0.5s ease-in-out';
            precanvas.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px) scale(${zoom})`;

            // Update Drawflow's internal state
            this.editor.canvas_x = newTranslateX;
            this.editor.canvas_y = newTranslateY;

            // Remove transition after animation completes so manual dragging isn't affected
            setTimeout(() => {
                precanvas.style.transition = '';
            }, 500);
        }
    }

    debouncedReflow(delay = 100) {
        if (this._reflowTimeout) {
            clearTimeout(this._reflowTimeout);
        }
        this._reflowTimeout = setTimeout(() => {
            this._reflowTimeout = null;
            this.reflowEntireTree();
        }, delay);
    }

    reflowEntireTree() {
        const allNodes = this.editor.drawflow.drawflow.Home.data;
        const minNodeSpacingY = 50; // Minimum vertical spacing between nodes
        const nodeSpacingX = 340; // Center-to-center horizontal spacing between sibling nodes
        const minNodeWidth = 240; // Minimum space for a node

        // Build a tree structure first
        const tree = this.buildTreeStructure(allNodes);

        // Helper function to get actual node height from DOM
        const getNodeHeight = (nodeId) => {
            const element = document.getElementById(`node-${nodeId}`);
            if (element) {
                // Get the actual rendered height including any expanded content
                return element.offsetHeight || 200;
            }
            return 200; // Default height if element not found
        };

        // Calculate the width needed for each subtree
        const calculateSubtreeWidth = (node) => {
            if (!node.children || node.children.length === 0) {
                return minNodeWidth;
            }

            let totalChildrenWidth = 0;
            node.children.forEach(child => {
                child.subtreeWidth = calculateSubtreeWidth(child);
                totalChildrenWidth += child.subtreeWidth;
            });

            // Add spacing between children
            totalChildrenWidth += (node.children.length - 1) * (nodeSpacingX - minNodeWidth);

            // The subtree width is the maximum of the node's own width and its children's combined width
            return Math.max(minNodeWidth, totalChildrenWidth);
        };

        // Position nodes based on calculated widths and actual heights
        const positionNode = (node, x, y) => {
            const nodeData = allNodes[node.id];
            if (!nodeData) return;

            // Update position in data
            nodeData.pos_x = x;
            nodeData.pos_y = y;

            // Update DOM position
            const element = document.getElementById(`node-${node.id}`);
            if (element) {
                element.style.left = x + 'px';
                element.style.top = y + 'px';
            }

            // Get the actual height of this node
            const nodeHeight = getNodeHeight(node.id);

            // Position children
            if (node.children && node.children.length > 0) {
                // Calculate child Y position based on actual parent node height
                const childY = y + nodeHeight + minNodeSpacingY;

                if (node.children.length === 1) {
                    // Single child - center it under parent
                    positionNode(node.children[0], x, childY);
                } else {
                    // Multiple children - distribute based on their subtree widths
                    let currentX = x - (node.subtreeWidth / 2) + (node.children[0].subtreeWidth / 2);

                    node.children.forEach((child, index) => {
                        positionNode(child, currentX, childY);

                        // Move to next child position
                        if (index < node.children.length - 1) {
                            const currentChildHalfWidth = child.subtreeWidth / 2;
                            const nextChildHalfWidth = node.children[index + 1].subtreeWidth / 2;
                            currentX += currentChildHalfWidth + Math.max(nodeSpacingX - minNodeWidth, 40) + nextChildHalfWidth;
                        }
                    });
                }
            }

            // Update connections for this node
            this.editor.updateConnectionNodes(`node-${node.id}`);
        };

        // Process each root and calculate total width needed
        if (tree.roots.length > 0) {
            let totalRootWidth = 0;
            tree.roots.forEach(root => {
                root.subtreeWidth = calculateSubtreeWidth(root);
                totalRootWidth += root.subtreeWidth;
            });

            // Add spacing between roots
            totalRootWidth += (tree.roots.length - 1) * nodeSpacingX;

            // Center the entire tree on the canvas
            const canvasWidth = this.editor.container.clientWidth || 1200;
            const startX = Math.max(400, (canvasWidth - totalRootWidth) / 2);

            // Position each root tree
            let currentX = startX;
            tree.roots.forEach((root, index) => {
                const rootX = currentX + (root.subtreeWidth / 2);
                positionNode(root, rootX, 50);
                currentX += root.subtreeWidth + (index < tree.roots.length - 1 ? nodeSpacingX : 0);
            });
        }
    }

    buildTreeStructure(allNodes) {
        const nodeMap = new Map();
        const roots = [];

        // Create node entries
        Object.keys(allNodes).forEach(nodeId => {
            nodeMap.set(nodeId, {
                id: nodeId,
                children: [],
                parent: null,
                subtreeWidth: 0
            });
        });

        // Build parent-child relationships
        Object.keys(allNodes).forEach(nodeId => {
            const nodeData = allNodes[nodeId];
            const node = nodeMap.get(nodeId);

            if (nodeData.inputs && nodeData.inputs.input_1 && nodeData.inputs.input_1.connections) {
                nodeData.inputs.input_1.connections.forEach(conn => {
                    const parentNode = nodeMap.get(conn.node);
                    if (parentNode) {
                        node.parent = parentNode;
                        if (!parentNode.children.find(c => c.id === nodeId)) {
                            parentNode.children.push(node);
                        }
                    }
                });
            }
        });

        // Find roots (nodes without parents)
        nodeMap.forEach(node => {
            if (!node.parent) {
                roots.push(node);
            }
        });

        return { roots, nodeMap };
    }

    loadTemplateSteps(steps) {
        this.isLoadingExistingData = true;

        const nodeMap = new Map();
        let yOffset = 50;

        const startId = this.addNode('start', 400, yOffset, {
            templateType: 'Form',
            templateName: 'Workflow'
        });
        nodeMap.set('start', startId);
        yOffset += 150;

        const stepsByParent = {};
        const rootSteps = [];

        steps.forEach(step => {
            if (step.parent_type === 'templates') {
                rootSteps.push(step);
            } else if (step.parent_type === 'template_steps') {
                const parentKey = `step_${step.parent_id}`;
                if (!stepsByParent[parentKey]) {
                    stepsByParent[parentKey] = [];
                }
                stepsByParent[parentKey].push(step);
            }
        });

        const createNodesForSteps = (parentSteps, parentNodeId, xPosition = 400, yPosition = yOffset) => {
            if (!parentSteps || parentSteps.length === 0) return;

            const xSpacing = 340;
            const totalWidth = (parentSteps.length - 1) * xSpacing;
            let startX = xPosition - totalWidth / 2;

            parentSteps.forEach((step, index) => {
                const x = startX + (index * xSpacing);
                const y = yPosition;

                const nodeType = step.type || 'unknown';

                let nodeData = {
                    stepId: step.id,
                    contentId: step.content_id,
                    name: step.name,
                    type: nodeType
                };

                if (nodeType === 'approval') {
                    nodeData.approvers = step.approvers_display || 'Not configured';
                    nodeData.approversList = step.approvers || [];
                    nodeData.requiresEsignature = step.requiresEsignature || false;
                    nodeData.quorumType = step.quorumType || 'all';
                } else if (nodeType === 'lookup') {
                    // Rendered through the approval node renderer with a routing badge.
                    nodeData.name = step.name || 'Approval Step';
                    nodeData.routedByTable = true;
                    nodeData.lookupFieldName = step.lookup_field_name || 'No field selected';
                    nodeData.lookupEntryCount = step.lookup_entry_count || 0;
                    nodeData.fallbackBehavior = step.fallback_behavior || 'skip';
                } else if (nodeType === 'job') {
                    nodeData.actionSlug = step.actionSlug || null;
                    nodeData.actionLabel = step.actionLabel || null;
                    nodeData.summary = step.summary || null;
                    nodeData.tested = step.tested || false;
                    nodeData.hasChildren = step.hasChildren ?? false;
                }

                const nodeId = this.addNode(nodeType, x, y, nodeData);
                nodeMap.set(`step_${step.id}`, nodeId);

                if (parentNodeId) {
                    this.editor.addConnection(parentNodeId, nodeId, 'output_1', 'input_1');
                }

                const childKey = `step_${step.id}`;
                if (stepsByParent[childKey]) {
                    createNodesForSteps(stepsByParent[childKey], nodeId, x, y + 200);
                }
            });
        };

        if (rootSteps.length > 0) {
            createNodesForSteps(rootSteps, startId, 400, yOffset);
        }

        this.isLoadingExistingData = false;

        // After loading all nodes, reflow the tree to prevent overlapping
        this.reflowEntireTree();

        // Center the view at the top
        setTimeout(() => {
            this.centerViewAtTop();
        }, 150);

        this.updateAllConnections();
    }

    updateAllConnections() {
        // Debounce connection updates to prevent redundant work when called multiple times
        if (this._updateConnectionsTimeout) {
            clearTimeout(this._updateConnectionsTimeout);
        }
        this._updateConnectionsTimeout = setTimeout(() => {
            this._updateConnectionsTimeout = null;
            Object.keys(this.editor.drawflow.drawflow.Home.data).forEach(nodeId => {
                this.editor.updateConnectionNodes(`node-${nodeId}`);
            });
        }, 200);
    }

    loadEmptyWorkflow(templateData = {}) {
        this.isLoadingExistingData = true;

        // Only create a start node
        const startId = this.addNode('start', 400, 50, {
            templateType: templateData.templateType || 'Form',
            templateName: templateData.templateName || 'Workflow'
        });

        this.isLoadingExistingData = false;

        this.updateAllConnections();

        // Center the view at the top
        setTimeout(() => {
            this.centerViewAtTop();
        }, 150);
    }

    openApproversModal(stepId) {
        if (window.Livewire) {
            window.Livewire.dispatch('modal.open', {
                component: 'flow-builder.add-approvers-modal',
                arguments: { step: stepId }
            });
        }
    }

    openJobConfigModal(stepId) {
        // Job steps configure through the unified step modal (action mode).
        this.openApproversModal(stepId);
    }

    openConditionConfigModal(stepId) {
        // TODO: Implement condition configuration modal
        alert('Condition configuration modal coming soon for step: ' + stepId);
    }

    syncWithLivewire(event, data) {
        if (this.$wire) {
            switch(event) {
                case 'nodeCreated':
                    this.$wire.handleNodeCreated(data);
                    break;
                case 'nodeRemoved':
                    this.$wire.handleNodeRemoved(data);
                    break;
                case 'nodeMoved':
                    this.$wire.handleNodeMoved(data);
                    break;
                case 'connectionCreated':
                    this.$wire.handleConnectionCreated(data);
                    break;
                case 'connectionRemoved':
                    this.$wire.handleConnectionRemoved(data);
                    break;
                default:
                    this.$wire.dispatch('flowBuilder:' + event, data);
            }
        } else if (window.Livewire) {
            window.Livewire.dispatch('flowBuilder:' + event, data);
        }
    }

    exportData() {
        return this.editor.export();
    }

    importData(data) {
        // Set flag to prevent creating DB records while importing
        this.isLoadingExistingData = true;

        this.editor.import(data);

        // Reset flag after import is complete
        this.isLoadingExistingData = false;

        // After importing, reflow the tree to ensure proper spacing
        this.reflowEntireTree();

        // Center the view at the top
        setTimeout(() => {
            this.centerViewAtTop();
        }, 150);
    }

    clear() {
        this.editor.clear();
    }
}

// Create instance but don't initialize automatically
if (!window.flowBuilder) {
    window.flowBuilder = new FlowBuilderManager();
}

export default FlowBuilderManager;